import { Duration, Instant } from "@js-joda/core";
import { useEffect, useState } from "react";
import { useCallbackRef } from "./useCallbackRef";

type TimeUnit = "days" | "hours" | "minutes" | "seconds";

interface Countdown {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  totalSeconds: number;
  totalSecondsWithNegative: number;
  formatted: string;
}

function useInstantCountdown({
  targetInstant,
  biggestUnit = "days",
  onTimerEnd,
  onTick,
}: {
  targetInstant: Instant;
  biggestUnit?: TimeUnit;
  onTimerEnd?: () => void;
  onTick?: (timeLeft: Countdown) => void;
}): Countdown {
  const [timeLeft, setTimeLeft] = useState(() => calculateTimeLeft(targetInstant, biggestUnit));
  const onTimerEndRef = useCallbackRef(onTimerEnd);
  const onTickRef = useCallbackRef(onTick);

  useEffect(() => {
    const intervalId = setInterval(() => {
      const newTimeLeft = calculateTimeLeft(targetInstant, biggestUnit);

      setTimeLeft(newTimeLeft);

      onTickRef(newTimeLeft);

      if (
        newTimeLeft.days === 0 &&
        newTimeLeft.hours === 0 &&
        newTimeLeft.minutes === 0 &&
        newTimeLeft.seconds === 0
      ) {
        clearInterval(intervalId);
        onTimerEndRef();
      }
    }, 1000);

    return () => clearInterval(intervalId);
  }, [targetInstant, biggestUnit, onTimerEndRef, onTickRef]);

  return timeLeft;
}

function calculateTimeLeft(targetInstant: Instant, biggestUnit: TimeUnit): Countdown {
  const now = Instant.now();
  const duration = Duration.between(now, targetInstant);

  const totalSeconds = duration.seconds();

  if (duration.isNegative()) {
    return {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
      totalSeconds: 0,
      totalSecondsWithNegative: totalSeconds,
      formatted: "00:00:00:00",
    };
  }

  const days = Math.floor(totalSeconds / (3600 * 24));
  const hours = Math.floor((totalSeconds % (3600 * 24)) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const formatted = formatTime(days, hours, minutes, seconds, biggestUnit);

  return {
    days,
    hours,
    minutes,
    seconds,
    totalSeconds,
    totalSecondsWithNegative: totalSeconds,
    formatted,
  };
}

function formatTime(
  days: number,
  hours: number,
  minutes: number,
  seconds: number,
  biggestUnit: TimeUnit
): string {
  switch (biggestUnit) {
    case "days":
      return `${padNumber(days)}:${padNumber(hours)}:${padNumber(minutes)}:${padNumber(seconds)}`;

    case "hours": {
      const totalHours = days * 24 + hours;
      return `${padNumber(totalHours)}:${padNumber(minutes)}:${padNumber(seconds)}`;
    }

    case "minutes": {
      const totalMinutes = (days * 24 + hours) * 60 + minutes;
      return `${padNumber(totalMinutes)}:${padNumber(seconds)}`;
    }

    case "seconds": {
      const totalSeconds = ((days * 24 + hours) * 60 + minutes) * 60 + seconds;
      return `${totalSeconds}`;
    }
  }
}

function padNumber(num: number): string {
  return num.toString().padStart(2, "0");
}

export default useInstantCountdown;
